"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  Link,
  Spinner,
  Toast,
  Toaster,
  ToastTitle,
  ToastTrigger,
  useId,
  useToastController,
} from "@fluentui/react-components";
import MaterialReactTable, {
  MRT_ColumnDef,
  MRT_RowSelectionState,
} from "material-react-table";
import ASCButton from "../../../components/Button";
import apiServices from "../../../service";
import { formatedDate } from "../../../utils";
import HighlightText from "../../../components/HighlightText";
import { withCurrencyColumns } from "../../../components/Common/currencyColumn";

// ── Types ────────────────────────────────────────────────────────────────────

type CaseEndpoint = "asset" | "liability" | "policy";

interface CaseTypeOption {
  label: string;
  endpoint: CaseEndpoint;
  primaryKey: string;
}

// ── Dropdown options ──────────────────────────────────────────────────────────

const CASE_TYPE_OPTIONS: CaseTypeOption[] = [
  { label: "Asset", endpoint: "asset", primaryKey: "asset_id" },
  { label: "Liability", endpoint: "liability", primaryKey: "liability_id" },
  { label: "Policy", endpoint: "policy", primaryKey: "policy_id" },
];

// ── Column definitions per case type ─────────────────────────────────────────
//
// Field names are taken from the actual API responses:
//   GET /case/asset/customer/{id}
//   GET /case/liability/customer/{id}
//   GET /case/policy/customer/{id}

// Asset case_type values as returned by the API don't always match how we want
// them labelled in the table — e.g. "Unit-linked" should read as "Asset(Investment)".
const ASSET_TYPE_DISPLAY_LABELS: Record<string, string> = {
  "unit-linked": "Asset (Investment)",
};

function getAssetTypeDisplayLabel(caseType: string): string {
  if (!caseType) return caseType;
  return ASSET_TYPE_DISPLAY_LABELS[caseType.trim().toLowerCase()] ?? caseType;
}

const ASSET_COLUMNS: MRT_ColumnDef<any>[] = [
  {
    accessorKey: "case_type",
    header: "Type",
    size: 70,
    minSize: 60,
    Cell: ({ cell }) => getAssetTypeDisplayLabel(cell.getValue<string>()),
  },
  { accessorKey: "subtype", header: "Subtype", size: 90, minSize: 70 },
  { accessorKey: "provider", header: "Provider", size: 130, minSize: 90 },
  {
    accessorKey: "start_date",
    header: "Start Date",
    filterFn: (row: any, columnId: any, filterValue: any) =>
      formatedDate(row.getValue(columnId))
        .toLowerCase()
        .includes(String(filterValue).trim().toLowerCase()),
    Cell: ({ row, column }: any) => (
      <HighlightText
        text={formatedDate(row.original.start_date)}
        highlight={String(column.getFilterValue() ?? "")}
      />
    ),
    size: 90,
    minSize: 80,
  },
  { accessorKey: "policy_number", header: "Reference", size: 100, minSize: 80 },
  { accessorKey: "status", header: "Status", size: 70, minSize: 60 },
  {
    accessorKey: "current_value",
    header: "Surrender Value",
    size: 110,
    minSize: 90,
  },
];

const LIABILITY_COLUMNS: MRT_ColumnDef<any>[] = [
  { accessorKey: "case_type", header: "Type", size: 70, minSize: 60 },
  { accessorKey: "subtype", header: "Subtype", size: 90, minSize: 70 },
  { accessorKey: "provider", header: "Provider", size: 130, minSize: 90 },
  {
    accessorKey: "start_date",
    header: "Start Date",
    filterFn: (row: any, columnId: any, filterValue: any) =>
      formatedDate(row.getValue(columnId))
        .toLowerCase()
        .includes(String(filterValue).trim().toLowerCase()),
    Cell: ({ row, column }: any) => (
      <HighlightText
        text={formatedDate(row.original.start_date)}
        highlight={String(column.getFilterValue() ?? "")}
      />
    ),
    size: 90,
    minSize: 80,
  },
  { accessorKey: "term", header: "Term", size: 60, minSize: 50 },
  { accessorKey: "policy_number", header: "Reference", size: 100, minSize: 80 },
  { accessorKey: "status", header: "Status", size: 70, minSize: 60 },
  {
    accessorKey: "amount_outstanding",
    header: "Surrender Value",
    size: 110,
    minSize: 90,
  },
  { accessorKey: "premium", header: "Premium", size: 80, minSize: 65 },
  { accessorKey: "frequency", header: "Frequency", size: 80, minSize: 65 },
];

const POLICY_COLUMNS: MRT_ColumnDef<any>[] = [
  { accessorKey: "case_type", header: "Type", size: 70, minSize: 60 },
  { accessorKey: "subtype", header: "Subtype", size: 90, minSize: 70 },
  { accessorKey: "provider", header: "Provider", size: 130, minSize: 90 },
  {
    accessorKey: "start_date",
    header: "Start Date",
    filterFn: (row: any, columnId: any, filterValue: any) =>
      formatedDate(row.getValue(columnId))
        .toLowerCase()
        .includes(String(filterValue).trim().toLowerCase()),
    Cell: ({ row, column }: any) => (
      <HighlightText
        text={formatedDate(row.original.start_date)}
        highlight={String(column.getFilterValue() ?? "")}
      />
    ),
    size: 90,
    minSize: 80,
  },
  { accessorKey: "term", header: "Term", size: 60, minSize: 50 },
  { accessorKey: "policy_number", header: "Reference", size: 100, minSize: 80 },
  { accessorKey: "status", header: "Status", size: 70, minSize: 60 },
  {
    accessorKey: "surrender_value",
    header: "Surrender Value",
    size: 110,
    minSize: 90,
  },
  { accessorKey: "premium", header: "Premium", size: 80, minSize: 65 },
  { accessorKey: "frequency", header: "Frequency", size: 80, minSize: 65 },
  { accessorKey: "benefit", header: "Benefit", size: 80, minSize: 65 },
  {
    accessorKey: "periodic_benefit",
    header: "Periodic Benefit",
    size: 110,
    minSize: 90,
  },
  {
    accessorKey: "benefit_frequency",
    header: "Benefit Frequency",
    size: 110,
    minSize: 90,
  },
];

const COLUMNS_BY_ENDPOINT: Record<CaseEndpoint, MRT_ColumnDef<any>[]> = {
  asset: withCurrencyColumns(ASSET_COLUMNS),
  liability: withCurrencyColumns(LIABILITY_COLUMNS),
  // POLICY scopes `benefit`, which is only an amount on policy grids.
  policy: withCurrencyColumns(POLICY_COLUMNS, "POLICY"),
};

// ── Field normalisers ─────────────────────────────────────────────────────────
//
// The API returns type-specific field names for Asset (investment_subtype,
// bank_name, etc.) and a few name differences for Liability/Policy. These
// functions map every row to the generic snake_case names the column
// accessorKeys expect, while preserving all original fields.

function normalizeAsset(row: any): any {
  const ct: string = (row.case_type ?? "").toLowerCase();

  let subtype = row.subtype ?? "";
  let provider = row.provider ?? "";
  let startDate = row.start_date ?? "";
  let reference = row.policy_number ?? "";
  let status = row.status ?? "";

  if (ct.includes("unit") || ct.includes("linked")) {
    subtype = row.investment_subtype ?? subtype;
    provider = row.investment_provider ?? provider;
    startDate = row.investment_start_date ?? startDate;
    reference = row.investment_account_number ?? reference;
    status = row.investment_status ?? status;
  } else if (ct.includes("share")) {
    subtype = row.share_subtype ?? subtype;
    provider = row.share_company ?? provider;
    startDate = row.share_date_acquired ?? startDate;
    reference = row.share_account_number ?? reference;
    status = row.share_status ?? status;
  } else if (ct.includes("bank") || ct.includes("societ")) {
    provider = row.bank_name ?? provider;
    startDate = row.bank_date_opened ?? startDate;
    reference = row.bank_account_number ?? reference;
    status = row.bank_status ?? status;
  } else if (ct.includes("home") || ct.includes("personal")) {
    startDate = row.home_date_acquired ?? startDate;
  }

  return {
    ...row,
    subtype,
    provider,
    start_date: startDate,
    policy_number: reference,
    status,
    current_value: row.current_value ?? row.investment_surrender_value ?? "",
  };
}

function normalizeLiability(row: any): any {
  return {
    ...row,
    // Liability API returns account_number; column expects policy_number (Reference)
    policy_number: row.policy_number ?? row.account_number ?? "",
  };
}

function normalizePolicy(row: any): any {
  return {
    ...row,
    // Policy API returns lump_sum_benefit; column expects benefit
    benefit: row.benefit ?? row.lump_sum_benefit ?? "",
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface CaseSelectionModalProps {
  open: boolean;
  customerId: string;
  onSelect: (caseId: string, displayText: string) => void;
  onClose: () => void;
}

const CaseSelectionModal: React.FC<CaseSelectionModalProps> = ({
  open,
  customerId,
  onSelect,
  onClose,
}) => {
  const [selectedType, setSelectedType] = useState<CaseTypeOption>(
    CASE_TYPE_OPTIONS[0],
  );
  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string>("");
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const toasterId = useId("case-selection-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const notify = (message: string, type: "error" | "success" = "error") =>
    dispatchToast(
      <Toast>
        <ToastTitle
          action={
            <ToastTrigger>
              <Link>Dismiss</Link>
            </ToastTrigger>
          }
        >
          {message}
        </ToastTitle>
      </Toast>,
      { intent: type },
    );

  // Tracks the most recently issued fetch so a slower, superseded request
  // (e.g. from rapidly switching Asset/Liability/Policy) can't overwrite
  // the UI after a later request has already resolved.
  const requestIdRef = useRef(0);

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => COLUMNS_BY_ENDPOINT[selectedType.endpoint],
    [selectedType.endpoint],
  );

  // Reset state whenever the modal opens
  useEffect(() => {
    if (!open) return;
    setRowSelection({});
    setCases([]);
    setFetchError("");
    setSelectedType(CASE_TYPE_OPTIONS[0]);
  }, [open]);

  // Re-fetch whenever the endpoint changes
  useEffect(() => {
    if (!open || !customerId) return;
    fetchCases(selectedType.endpoint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedType.endpoint, customerId]);

  const fetchCases = async (endpoint: CaseEndpoint) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setCases([]);
    setRowSelection({});
    setFetchError("");
    try {
      let resp: any;
      if (endpoint === "asset") {
        resp = await apiServices.caseManagement.getAssetCases(customerId);
      } else if (endpoint === "liability") {
        resp = await apiServices.caseManagement.getLiabilityCases(customerId);
      } else {
        resp = await apiServices.caseManagement.getPolicyCases(customerId);
      }

      // A newer request has since been issued (user switched type/customer again) — discard this response.
      if (requestIdRef.current !== requestId) return;

      const raw: any[] = Array.isArray(resp?.data?.data)
        ? resp.data.data
        : Array.isArray(resp?.data)
          ? resp.data
          : [];

      const normalise =
        endpoint === "asset"
          ? normalizeAsset
          : endpoint === "liability"
            ? normalizeLiability
            : normalizePolicy;

      setCases(raw.map(normalise));
    } catch (err: any) {
      if (requestIdRef.current !== requestId) return;
      setCases([]);
      const message =
        err?.message || `Failed to load ${endpoint} cases. Please try again.`;
      setFetchError(message);
      notify(message, "error");
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const option = CASE_TYPE_OPTIONS.find((o) => o.endpoint === e.target.value);
    if (option) {
      setRowSelection({});
      setSelectedType(option);
    }
  };

  const handleConfirm = () => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return;
    const caseId = selectedIds[0];
    const selectedCase = cases.find(
      (c) => String(c[selectedType.primaryKey]) === caseId,
    );
    const ref =
      selectedCase?.policy_number || selectedCase?.account_number || "";
    const displayText = selectedCase
      ? `${selectedCase.case_type ?? selectedType.label}${ref ? ` (${ref})` : ""}`
      : selectedType.label;
    onSelect(caseId, displayText);
  };

  const selectedIds = Object.keys(rowSelection);
  const hasSelection = selectedIds.length > 0;

  const selectionLabel = hasSelection
    ? (() => {
        const sel = cases.find(
          (c) => String(c[selectedType.primaryKey]) === selectedIds[0],
        );
        if (!sel) return `Selected: ${selectedType.label}`;
        const ref = sel.policy_number || sel.account_number || "";
        return `Selected: ${getAssetTypeDisplayLabel(sel.case_type) ?? selectedType.label}${ref ? ` (${ref})` : ""}`;
      })()
    : "No case selected";

  return (
    <Dialog modalType="alert" open={open}>
      <Toaster
        toasterId={toasterId}
        position="top-end"
        pauseOnHover
        pauseOnWindowBlur
        timeout={4000}
      />
      <DialogSurface
        style={{
          maxWidth: 800,
          minHeight: 480,
          padding: 0,
          overflow: "hidden",
        }}
      >
        <DialogBody
          style={{
            padding: 0,
            gap: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            maxHeight: "88vh",
          }}
        >
          {/* ── Header ── */}
          <div
            style={{
              padding: "10px 20px",
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #e8ecf3",
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600 }}>Select Case</span>
          </div>

          <DialogContent
            style={{ padding: "14px 20px", flex: 1, overflow: "auto" }}
          >
            {/* ── Case-type dropdown ── */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <div
                style={{ position: "relative", width: 190, marginLeft: "auto" }}
              >
                <select
                  value={selectedType.endpoint}
                  onChange={handleTypeChange}
                  style={{
                    width: "100%",
                    height: 32,
                    border: "1px solid #d1d9e6",
                    borderRadius: 5,
                    padding: "0 28px 0 8px",
                    fontSize: 13,
                    fontFamily: "inherit",
                    color: "#1a202c",
                    background: "#fff",
                    outline: "none",
                    appearance: "none",
                    cursor: "pointer",
                  }}
                >
                  {CASE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.endpoint} value={opt.endpoint}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    color: "#9aa5b8",
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M2 4l3 3 3-3"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>

            {/* ── Case table ── */}
            <div
              style={{
                minHeight: 280,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                    minHeight: 280,
                  }}
                >
                  <Spinner size="medium" />
                </div>
              ) : fetchError ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                    minHeight: 280,
                    gap: 8,
                    color: "#b91c1c",
                    fontSize: 13,
                    textAlign: "center",
                    padding: "0 24px",
                  }}
                >
                  <span>{fetchError}</span>
                </div>
              ) : (
                <div className="table-in-popup">
                  <MaterialReactTable
                    columns={columns}
                    data={cases}
                    enableRowSelection
                    enableMultiRowSelection={false}
                    muiSelectCheckboxProps={{ type: "radio" } as any}
                    onRowSelectionChange={setRowSelection}
                    state={{ rowSelection }}
                    getRowId={(row, index) => {
                      const id = row?.[selectedType.primaryKey];
                      return id !== undefined && id !== null && id !== ""
                        ? String(id)
                        : `__no_id_${index}`;
                    }}
                    enableColumnActions={false}
                    enableColumnFilters={false}
                    enableGlobalFilter={true}
                    enableFullScreenToggle={false}
                    muiTableProps={{ style: { tableLayout: "auto" } }}
                    muiTableContainerProps={{
                      style: { maxHeight: 280, overflowY: "auto" },
                    }}
                    muiTableHeadCellProps={{
                      style: {
                        background: "#f5f7fb",
                        padding: "5px 8px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#5a6a85",
                        whiteSpace: "nowrap",
                      },
                    }}
                    muiTableBodyCellProps={{
                      style: {
                        padding: "6px 8px",
                        fontSize: "0.78rem",
                        whiteSpace: "nowrap",
                      },
                    }}
                    muiTablePaginationProps={{
                      showFirstButton: false,
                      showLastButton: false,
                    }}
                  />
                </div>
              )}
            </div>
          </DialogContent>

          {/* ── Footer ── */}
          <DialogActions
            style={{
              padding: "11px 20px",
              borderTop: "1px solid #e8ecf3",
              flexShrink: 0,
              background: "#f8f9fc",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span
              style={{ marginRight: "auto", fontSize: 12, color: "#5a6a85" }}
            >
              {selectionLabel}
            </span>
            <ASCButton
              shape="rounded"
              appearance="secondary"
              onClick={onClose}
              label="Cancel"
            />
            <ASCButton
              shape="rounded"
              appearance="primary"
              className="asc-button-primary"
              onClick={handleConfirm}
              label="Select"
              disabled={!hasSelection}
            />
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default CaseSelectionModal;
